import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import MuestrasMovimientoCamara from "./MuestrasMovimientoCamara";
import { exportMuestrasToPdf } from "./muestrasPdfExport";
import { canSharePdfFile, isIosDevice } from "./muestrasPdfDelivery";

const makeFilename = () =>
  `Muestras_SDS_${new Date().toISOString().slice(0, 10)}.pdf`;

const MuestrasPresentacionCamara = ({ muestras = [], embedded = false }) => {
  const [preparedPdf, setPreparedPdf] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");

  useEffect(
    () => () => {
      if (preparedPdf?.url) URL.revokeObjectURL(preparedPdf.url);
    },
    [preparedPdf],
  );

  const generatePresentation = useCallback(
    async ({ items, setProgress }) => {
      const selectedIds = items.map((item) => Number(item.ID_MUESTRA));
      const selectedSet = new Set(selectedIds);
      const selectedMuestras = muestras.filter((muestra) =>
        selectedSet.has(Number(muestra.id ?? muestra.ID_MUESTRA)),
      );

      if (selectedMuestras.length !== selectedSet.size) {
        throw new Error(
          "No se han podido recuperar todas las muestras seleccionadas.",
        );
      }

      const filename = makeFilename();
      const ios = isIosDevice();
      const blob = await exportMuestrasToPdf({
        muestras: selectedMuestras,
        selectedIds,
        filename,
        onProgress: ({ current, total }) => setProgress(current, total),
        download: !ios,
      });

      if (ios) {
        const file = new File([blob], filename, { type: "application/pdf" });
        setPreparedPdf((previous) => {
          if (previous?.url) URL.revokeObjectURL(previous.url);
          return {
            file,
            filename,
            url: URL.createObjectURL(blob),
          };
        });
        return {
          successMessage:
            "✅ Presentación preparada. Pulsa “Guardar o compartir PDF”.",
        };
      }

      return { successMessage: "✅ Presentación descargada." };
    },
    [muestras],
  );

  const sharePreparedPdf = async () => {
    if (!preparedPdf || sharing) return;
    setSharing(true);
    setShareError("");
    try {
      if (canSharePdfFile(navigator, preparedPdf.file)) {
        await navigator.share({
          files: [preparedPdf.file],
          title: "Presentación de muestras",
        });
        return;
      }
      window.open(preparedPdf.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      if (error?.name !== "AbortError") {
        setShareError(
          "No se pudo abrir el menú para guardar o compartir el PDF.",
        );
      }
    } finally {
      setSharing(false);
    }
  };

  const selectionFooter = preparedPdf ? (
    <Box sx={{ mt: 2, textAlign: "left" }}>
      <Alert severity="success" sx={{ mb: 1.5 }}>
        PDF preparado. En iPhone puedes guardarlo en Archivos, enviarlo o
        compartirlo por AirDrop.
      </Alert>
      <Button
        variant="contained"
        fullWidth
        onClick={sharePreparedPdf}
        disabled={sharing}
        startIcon={
          sharing ? (
            <CircularProgress size={17} color="inherit" />
          ) : (
            <IosShareOutlinedIcon />
          )
        }
      >
        Guardar o compartir PDF
      </Button>
      {shareError ? (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {shareError}
        </Typography>
      ) : null}
    </Box>
  ) : null;

  return (
    <MuestrasMovimientoCamara
      embedded={embedded}
      title={embedded ? null : "PRESENTACIÓN DE MUESTRAS"}
      confirmLabel="Generar presentación PDF"
      selectionOnly
      selectionListTitle="Presentación"
      customRegister={generatePresentation}
      selectionFooter={selectionFooter}
      hideCourier
    />
  );
};

export default MuestrasPresentacionCamara;
